import { CSSProperties, ComponentPropsWithoutRef, ElementRef, forwardRef } from "react";
import { Tabs as PrimitiveTabs, Slot } from "radix-ui";
import classNames from "classnames/bind";
import Typography, { TypographyVariant } from "app/pages/.shared/Typography/Typography";
import styles from "./Tabs.module.scss";

const cx = classNames.bind(styles);

export type TabsProps = ComponentPropsWithoutRef<typeof PrimitiveTabs.Root>;

export const Tabs = forwardRef<ElementRef<typeof PrimitiveTabs.Root>, TabsProps>(
	({ className, ...props }, ref) => (
		<PrimitiveTabs.Root ref={ref} className={cx("tabs", className)} {...props} />
	)
);
Tabs.displayName = "Tabs";

export type TabsListProps = ComponentPropsWithoutRef<typeof PrimitiveTabs.List> & {
	/** Stretches the list to its container and spreads the triggers evenly. */
	fullWidth?: boolean;
};

export const TabsList = forwardRef<ElementRef<typeof PrimitiveTabs.List>, TabsListProps>(
	({ className, fullWidth = false, ...props }, ref) => (
		<PrimitiveTabs.List
			ref={ref}
			className={cx("tabs__list", className)}
			data-full-width={fullWidth}
			{...props}
		/>
	)
);
TabsList.displayName = "TabsList";

export type TabsTriggerProps = ComponentPropsWithoutRef<typeof PrimitiveTabs.Trigger> & {
	/** Label weight, whatever the tab state. */
	isBold?: boolean;
	variant?: TypographyVariant;
	/** Any CSS color for the selected tab label and underline. Defaults to `--color-active`. */
	activeColor?: string;
};

export const TabsTrigger = forwardRef<ElementRef<typeof PrimitiveTabs.Trigger>, TabsTriggerProps>(
	(
		{
			className,
			children,
			asChild = false,
			isBold = true,
			variant = "regular",
			activeColor,
			style,
			...props
		},
		ref
	) => (
		<PrimitiveTabs.Trigger
			ref={ref}
			asChild
			style={{ ...style, "--tabs-active-color": activeColor } as CSSProperties}
			{...props}
		>
			<Typography
				component={asChild ? Slot.Root : "button"}
				variant={variant}
				isBold={isBold}
				className={cx("tabs__trigger", className)}
			>
				{children}
			</Typography>
		</PrimitiveTabs.Trigger>
	)
);
TabsTrigger.displayName = "TabsTrigger";

export type TabsContentProps = ComponentPropsWithoutRef<typeof PrimitiveTabs.Content>;

export const TabsContent = forwardRef<ElementRef<typeof PrimitiveTabs.Content>, TabsContentProps>(
	({ className, ...props }, ref) => (
		<PrimitiveTabs.Content ref={ref} className={cx("tabs__content", className)} {...props} />
	)
);
TabsContent.displayName = "TabsContent";
